import clsx from 'clsx';
import InfoTip from './InfoTip';

/**
 * Consistent header bar at the top of every dashboard tab / page.
 *
 *   <PageHeader
 *     icon={Sparkles}
 *     eyebrow="AI Optimization"
 *     title="Content gap analyzer"
 *     description="AI reads your top pages and finds AI-search questions your site doesn't answer yet."
 *     help={{ title: 'How this works', body: '…' }}
 *     actions={<button …/>}
 *   />
 *
 * Goal: every tab has a clear, scannable intro so a first-time user knows
 * exactly what the screen does.
 */
export default function PageHeader({
  icon: Icon,
  eyebrow,
  title,
  description,
  help, // { title, body } — wraps an InfoTip after the title
  actions,
  className = '',
  variant = 'default', // 'default' | 'gradient'
}) {
  const isGradient = variant === 'gradient';
  return (
    <div
      className={clsx(
        'relative',
        isGradient
          ? 'rounded-2xl bg-ink-gradient text-white p-6 sm:p-7 shadow-xl'
          : 'mb-2',
        className,
      )}
    >
      {isGradient && (
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
          <div className="absolute inset-0 bg-brand-radial" />
          <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-cyan-500/20 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-brand-500/25 blur-3xl" />
        </div>
      )}

      <div className="relative flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex items-start gap-3">
          {Icon && (
            <div
              className={clsx(
                'flex-none flex items-center justify-center rounded-xl',
                isGradient
                  ? 'h-11 w-11 bg-white/15 text-white ring-1 ring-white/20 backdrop-blur'
                  : 'h-10 w-10 bg-gradient-to-br from-brand-500/15 to-cyan-500/15 text-brand-600 ring-1 ring-brand-100',
              )}
            >
              <Icon className={isGradient ? 'w-5 h-5' : 'w-5 h-5'} strokeWidth={1.75} />
            </div>
          )}
          <div className="min-w-0">
            {eyebrow && (
              <div
                className={clsx(
                  'text-[11px] font-semibold uppercase tracking-[0.18em]',
                  isGradient ? 'text-cyan-300' : 'text-brand-600',
                )}
              >
                {eyebrow}
              </div>
            )}
            <h1
              className={clsx(
                'mt-0.5 font-display tracking-tight',
                isGradient ? 'text-2xl sm:text-3xl text-white' : 'text-xl sm:text-2xl text-ink-900',
              )}
            >
              <span className="inline-flex items-center gap-2">
                {title}
                {help && (
                  <InfoTip
                    title={help.title}
                    body={help.body}
                    className={isGradient ? 'text-white/70 hover:text-white' : ''}
                  />
                )}
              </span>
            </h1>
            {description && (
              <p
                className={clsx(
                  'mt-1.5 text-sm leading-relaxed max-w-2xl text-balance',
                  isGradient ? 'text-white/80' : 'text-ink-400',
                )}
              >
                {description}
              </p>
            )}
          </div>
        </div>
        {actions && (
          <div className="flex-none flex items-center gap-2 flex-wrap">{actions}</div>
        )}
      </div>
    </div>
  );
}
